import React, { useMemo, useRef, useState } from 'react';

import { useContentfulInspectorMode } from '@contentful/live-preview/react';

import { Container, Row, Section } from 'src/components-v2/Layout';
import Box from 'src/components-v2/Layout/Box/Box';
import Column from 'src/components-v2/Layout/Grid/Column';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import { Typography } from 'src/components-v2/DataDisplay/Typography';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import type { SectionCalculatorEntry } from 'src/types/contentful';
import { Disclaimer } from '../Calculator/Calculator.styled';
import {
  RateSlider,
  RateTrackStopProperties,
} from './LendScoreCalculator.styled';
import { LendScoreCard } from './LendScoreCard';
import type { MetricKey } from './LendScoreCard';
import {
  adjacentApprovalRate,
  approvalRateNearLossRate,
  findRateSeries,
  projectFromApprovalRate,
} from './lendScoreCalculations';
import { useLendScoreTracking } from './useLendScoreTracking';
import Card from 'threads5/Card';
import FormControl from 'threads5/FormControl';
import Select, { renderSelectMenuItem } from 'threads5/Select';
import styled from 'threads5/styles/styled';

// The loan types map onto the sets of LendScore ROI figures, so they are fixed
// here rather than authored in Contentful. The same goes for the score models
// below.
const LOAN_TYPE_OPTIONS = [
  { label: 'All', value: 'overall' },
  { label: 'Auto', value: 'auto' },
  { label: 'Home', value: 'home' },
  { label: 'Short Term', value: 'shortTerm' },
] as const;

const SCORE_OPTIONS = [
  { label: 'LendScore', value: 'lendScore' },
  { label: 'Auto', value: 'auto' },
  { label: 'Home Lending', value: 'home' },
  { label: 'Short Term', value: 'shortTerm' },
] as const;

const SEGMENT_OPTIONS = [
  { label: 'Subprime', value: 'subprime' },
  { label: 'Prime', value: 'prime' },
] as const;

const SEGMENT_HEADING_ID = 'lend-score-calculator-segment-heading';

// The size of the segment cards, and the width they start from below `sm`.
// The mobile width is only where they start: two of them and the gap between
// are wider than most phones, so they shrink to fit the row.
const SEGMENT_CARD_WIDTH = 162;
const SEGMENT_CARD_HEIGHT = 56;
const SEGMENT_CARD_MOBILE_WIDTH = 173;

const RATE_STEP = 5;

// The delinquency slider's track always runs to the same rate, wherever the
// data for the selected loan type and segment stops, so a low-risk segment
// visibly sits near the left end instead of being stretched to fill the track.
const DELINQUENCY_RATE_MAX = 40;

// Only one rate drives the projection at a time, so the two sliders behave
// like a pair: touching one makes it the active input. The approval rate
// moves in whole steps. The delinquency rate has no step of its own, because
// the rates it can take are not evenly spaced: the thumb is snapped to the
// nearest one in the selected loan type and segment instead.
const RATE_SLIDERS = [
  {
    id: 'lend-score-calculator-approval-rate',
    key: 'approvalRate',
    label: 'Set your target approval rate',
    max: 100,
    step: RATE_STEP,
  },
  {
    id: 'lend-score-calculator-delinquency-rate',
    key: 'delinquencyRate',
    label: 'Set your target delinquency rate',
    max: DELINQUENCY_RATE_MAX,
    step: 'any',
  },
] as const;

// Arrow keys and the page keys step the delinquency slider one rate at a time.
// Left to the browser they would move it by a fixed amount, which is either
// less than the gap to the next rate (so the thumb would snap back and the
// slider would be stuck) or more (so it would skip rates).
const getNudgeDirection = (key: string): 1 | -1 | undefined => {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowUp':
    case 'PageUp':
      return 1;
    case 'ArrowLeft':
    case 'ArrowDown':
    case 'PageDown':
      return -1;
    default:
      return undefined;
  }
};

// A delinquency rate read off the curve rarely lands on a whole number, so it
// is shown to one decimal place, and without a trailing .0 when it does.
const formatRate = (rate: number) => {
  return `${Number(rate.toFixed(1))}%`;
};

// What a slider reads when there is no rate to read: an em dash, not a 0%
// that would pass for a rate the data gave.
const NO_RATE = '\u2014';

// Lets a flat colour sit in the same background layer as a gradient.
const asGradient = (color: string) => {
  return `linear-gradient(${color}, ${color})`;
};

// Keeps the radio focusable and operable while the Card provides the visuals.
const SegmentRadio = styled('input')(() => {
  return {
    opacity: 0,
    position: 'absolute',
  };
});

type LoanType = (typeof LOAN_TYPE_OPTIONS)[number]['value'];
type ScoreType = (typeof SCORE_OPTIONS)[number]['value'];
type Segment = (typeof SEGMENT_OPTIONS)[number]['value'];
type RateKey = (typeof RATE_SLIDERS)[number]['key'];

// Every loan type offers LendScore, plus the score model that belongs to it.
// All has no model of its own.
const SCORE_VALUES_BY_LOAN_TYPE: Record<LoanType, readonly ScoreType[]> = {
  overall: ['lendScore'],
  auto: ['lendScore', 'auto'],
  home: ['lendScore', 'home'],
  shortTerm: ['lendScore', 'shortTerm'],
};

const getScoreOptions = (loanType: LoanType) => {
  return SCORE_OPTIONS.filter((option) => {
    return SCORE_VALUES_BY_LOAN_TYPE[loanType].includes(option.value);
  });
};

type CalculatorSelectProps<V extends string> = {
  idPrefix: string;
  label: string;
  onChange: (value: V) => void;
  options: readonly { label: string; value: V }[];
  value: V;
};

const CalculatorSelect = <V extends string>({
  idPrefix,
  label,
  onChange,
  options,
  value,
}: CalculatorSelectProps<V>) => {
  const labelId = `${idPrefix}-label-id`;
  const selectId = `${idPrefix}-select-id`;

  return (
    <Stack gap={2}>
      {/* The label sits above the field rather than floating inside it, so it
          is a plain heading wired to the select through `labelId` instead of
          an `InputLabel`. Typography does not take an `id`, hence the span. */}
      <span id={labelId}>
        <Typography variant='p2' component='span' sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
      </span>
      <FormControl id={`${idPrefix}-form-ctrl`}>
        <Select
          id={selectId}
          labelId={labelId}
          size={48}
          fullWidth={true}
          value={value}
          onChange={(event) => {
            onChange(event.target.value as V);
          }}
        >
          {/* Menu items have to be rendered as immediate children of the menu,
              which is why threads5 exposes a render function instead of a
              component here. */}
          {options.map((option) => {
            return renderSelectMenuItem({
              key: option.value,
              value: option.value,
              children: option.label,
            });
          })}
        </Select>
      </FormControl>
    </Stack>
  );
};

type SegmentCardProps = {
  isSelected: boolean;
  label: string;
  onSelect: (value: Segment) => void;
  value: Segment;
};

const SegmentCard: React.FC<SegmentCardProps> = ({
  isSelected,
  label,
  onSelect,
  value,
}) => {
  return (
    <Card
      component='label'
      appearance='flat'
      sx={(theme) => {
        return {
          alignItems: 'center',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          // The size is the card's outer size, border included. The height is
          // fixed rather than following the shared Card's 24px padding, which
          // is dropped top and bottom so the label still fits inside it.
          boxSizing: 'border-box',
          width: `${SEGMENT_CARD_WIDTH}px`,
          height: `${SEGMENT_CARD_HEIGHT}px`,
          padding: '0 24px',
          flexShrink: 0,
          // Gradient borders can't go through the `border` shorthand, so the
          // fill is painted to the padding box and the border colour to the
          // border box. The flat colours go through the same two-layer
          // treatment so both states keep the same 2px border and selecting
          // one doesn't shift the layout.
          border: '2px solid transparent',
          background: isSelected
            ? `${theme.palette['gradient/background/stark/viola']} padding-box, ${theme.palette['gradient/graphic/subtle/elizabeth']} border-box`
            : `${asGradient(
                theme.palette['background/elevation-1'],
              )} padding-box, ${asGradient(
                theme.palette[
                  'graphic/interactive/neutral/resting/contrast-low'
                ],
              )} border-box`,
          '&:has(:focus-visible)': {
            outline: `2px solid ${theme.palette['graphic/contrast-high']}`,
            outlineOffset: '2px',
          },
          // Below `sm` the cards sit side by side and are allowed to shrink
          // to fit the row. Both start from the same width, so they give up
          // the same amount and stay in the same ratio. The side padding is
          // tightened so the label still fits as they shrink.
          [theme.breakpoints.down('sm')]: {
            flexShrink: 1,
            minWidth: 0,
            width: `${SEGMENT_CARD_MOBILE_WIDTH}px`,
            padding: '0 8px',
          },
        };
      }}
    >
      <SegmentRadio
        type='radio'
        name='lend-score-calculator-segment'
        value={value}
        checked={isSelected}
        onChange={() => {
          onSelect(value);
        }}
      />
      <Typography
        variant='p2'
        component='span'
        sx={{ fontWeight: isSelected ? 600 : 400 }}
      >
        {label}
      </Typography>
    </Card>
  );
};

type RateSliderFieldProps = {
  id: string;
  isActive: boolean;
  label: string;
  // The rate at the right end of the track. The left end is always 0%.
  max: number;
  onActivate: () => void;
  onChange: (value: number) => void;
  // Moves the slider to the next rate along instead of by a fixed amount.
  // Without it the browser moves the thumb by `step`.
  onNudge?: (direction: 1 | -1) => void;
  step: number | 'any';
  // The rate the slider sits on, which is also what it reads out. A rate the
  // data has no figure for is left out, and the thumb waits at 0%.
  value: number | undefined;
};

const RateSliderField: React.FC<RateSliderFieldProps> = ({
  id,
  isActive,
  label,
  max,
  onActivate,
  onChange,
  onNudge,
  step,
  value,
}) => {
  const position = value ?? 0;
  const readout = value === undefined ? NO_RATE : formatRate(value);

  return (
    <Stack gap={2}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <label htmlFor={id}>
          <Typography variant='p2' component='span' sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </label>
        <Typography variant='p2' component='span' sx={{ fontWeight: 600 }}>
          {readout}
        </Typography>
      </Stack>
      <RateSlider
        id={id}
        type='range'
        min={0}
        max={max}
        step={step}
        value={position}
        // A delinquency rate read off the curve has more decimal places than
        // the readout shows, so assistive technology is given the same text
        // rather than the raw value.
        aria-valuetext={readout}
        isActive={isActive}
        data-is-active={isActive}
        // min is 0, so the unfilled remainder of the track is the share of
        // the way to `max` the thumb has not yet come.
        style={
          {
            '--progress': `${100 - (position / max) * 100}%`,
          } as React.CSSProperties
        }
        onFocus={onActivate}
        onKeyDown={(event) => {
          const direction = getNudgeDirection(event.key);

          if (onNudge && direction) {
            event.preventDefault();
            onActivate();
            onNudge(direction);
          }
        }}
        onPointerDown={(event) => {
          // Picking up an inactive slider only selects it. The browser would
          // otherwise jump the thumb to the pointer at the same time, so the
          // click that chose the slider would also change its rate. Preventing
          // the default takes the focus with it, hence focusing by hand.
          if (!isActive) {
            event.preventDefault();
            event.currentTarget.focus();
          }

          onActivate();
        }}
        onChange={(event) => {
          onActivate();
          onChange(Number(event.target.value));
        }}
      />
    </Stack>
  );
};

type LendScoreCalculatorProps = {
  fields: SectionCalculatorEntry['fields'];
  sys: SectionCalculatorEntry['sys'];
};

export const LendScoreCalculator: React.FC<LendScoreCalculatorProps> = ({
  fields,
  sys,
}) => {
  const inspectorProps = useContentfulInspectorMode({ entryId: sys?.id });
  const sectionRef = useRef<HTMLElement>(null);
  const [loanType, setLoanType] = useState<LoanType>('overall');
  const [score, setScore] = useState<ScoreType>('lendScore');
  const [segment, setSegment] = useState<Segment>('subprime');
  // Each 5% step of approval has one delinquency rate that goes with it, so
  // the two sliders are two ways of moving along the same 20 steps. Only the
  // approval rate is stored. The delinquency rate is read off it, and moving
  // the delinquency slider picks the step whose rate is nearest.
  const [approvalRate, setApprovalRate] = useState(50);
  // Shared with the card's metric toggle, which is the same choice presented
  // as a pill: picking up a slider moves the toggle, and vice versa.
  const [activeRate, setActiveRate] = useState<MetricKey>('approvalRate');

  // The ROI data can come up without anything for a selection. The card then
  // explains that, and the delinquency rate, which is read off the data, has
  // nothing to read.
  const series = findRateSeries(loanType, score, segment);
  const projection = useMemo(() => {
    return series ? projectFromApprovalRate(series, approvalRate) : undefined;
  }, [series, approvalRate]);

  const rates: Record<RateKey, number | undefined> = {
    approvalRate,
    delinquencyRate: projection?.lossRate.without,
  };

  const { trackCtaClick, trackEngaged } = useLendScoreTracking({
    inputs: {
      approvalRate: rates.approvalRate,
      delinquencyRate: rates.delinquencyRate,
      loanType,
      metric: activeRate,
      score,
      segment,
    },
    sectionRef,
    trackingContext: fields?.trackingContext,
  });

  // Both sliders end up as a new approval rate, which is the only thing
  // stored.
  const moves: Record<
    RateKey,
    {
      onChange: (value: number) => void;
      onNudge?: (direction: 1 | -1) => void;
    }
  > = {
    approvalRate: {
      onChange: (value) => {
        trackEngaged('approvalRate', value);
        setApprovalRate(value);
      },
    },
    delinquencyRate: {
      onChange: (lossRate) => {
        if (series) {
          trackEngaged('delinquencyRate', lossRate);
          setApprovalRate(approvalRateNearLossRate(series, lossRate));
        }
      },
      // The arrow keys skip `onChange` and only know which way they went, so
      // the rate they land on is worked out for the event. At either end
      // there is nowhere to go, which isn't a change.
      onNudge: (direction) => {
        if (!series) return;

        const next = adjacentApprovalRate(series, approvalRate, direction);

        if (next === approvalRate) return;

        trackEngaged(
          'delinquencyRate',
          projectFromApprovalRate(series, next).lossRate.without,
        );
        setApprovalRate(next);
      },
    },
  };

  const hasH1 = fields?.formBody?.content?.some((node) => {
    return node.nodeType === 'heading-1';
  });

  return (
    <Section
      ref={sectionRef}
      component={hasH1 ? 'header' : 'section'}
      {...inspectorProps({ fieldId: 'internalTitle' })}
      sx={fields?.styles}
    >
      {/* Declared once for the section rather than per slider, since the
          registrations are global either way. */}
      <RateTrackStopProperties />
      <Container>
        <Row>
          <Column
            xsOffset={1}
            xs={22}
            smOffset={0}
            sm={24}
            lgOffset={2}
            lg={20}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              gap={{ xs: 5.25, sm: 9.125, lg: 17.5 }}
            >
              <Stack gap={5} sx={{ flex: 1, minWidth: 0 }}>
                <RichTextSwitch
                  sys={sys}
                  content={fields?.formBody}
                  nodes={{
                    h1: {
                      variant: 'h3',
                      component: 'h1',
                      fieldId: 'formBody',
                      sx: { fontSize: { xs: '2.4rem', sm: '4.0rem' } },
                    },
                    h2: {
                      variant: 'h3',
                      component: 'h2',
                      fieldId: 'formBody',
                      sx: { fontSize: { xs: '2.4rem', sm: '4.0rem' } },
                    },
                    p: {
                      variant: 'p',
                      component: 'p',
                      fieldId: 'formBody',
                      sx: { fontSize: { xs: '1.8rem', sm: '2.0rem' } },
                    },
                  }}
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 2, sm: 3 },
                  }}
                >
                  <CalculatorSelect
                    idPrefix='lend-score-calculator-loan-type'
                    label='Loan type'
                    options={LOAN_TYPE_OPTIONS}
                    value={loanType}
                    onChange={(value) => {
                      trackEngaged('loanType', value);
                      setLoanType(value);
                      // A model the new loan type doesn't offer can't stay
                      // selected, so the choice falls back to LendScore.
                      setScore((current) => {
                        const isOffered = getScoreOptions(value).some(
                          (option) => {
                            return option.value === current;
                          },
                        );

                        return isOffered ? current : 'lendScore';
                      });
                    }}
                  />
                  <CalculatorSelect
                    idPrefix='lend-score-calculator-score'
                    label='LendScore model'
                    options={getScoreOptions(loanType)}
                    value={score}
                    onChange={(value) => {
                      trackEngaged('score', value);
                      setScore(value);
                    }}
                  />
                </Box>
                <Stack gap={2}>
                  <span id={SEGMENT_HEADING_ID}>
                    <Typography
                      variant='p2'
                      component='span'
                      sx={{ fontWeight: 600 }}
                    >
                      Segment
                    </Typography>
                  </span>
                  <Box
                    role='radiogroup'
                    aria-labelledby={SEGMENT_HEADING_ID}
                    sx={(theme) => {
                      return {
                        // The cards are a fixed width rather than a grid
                        // track, so they wrap instead of stretching, and
                        // `flex-start` keeps their height on their own
                        // content.
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: { xs: 2, sm: 3 },
                        // Below `sm` they stay on one row instead, and the
                        // cards shrink to fit it.
                        [theme.breakpoints.down('sm')]: {
                          flexWrap: 'nowrap',
                        },
                      };
                    }}
                  >
                    {SEGMENT_OPTIONS.map((option) => {
                      return (
                        <SegmentCard
                          key={option.value}
                          label={option.label}
                          value={option.value}
                          isSelected={segment === option.value}
                          onSelect={(value) => {
                            trackEngaged('segment', value);
                            setSegment(value);
                          }}
                        />
                      );
                    })}
                  </Box>
                </Stack>
                {RATE_SLIDERS.map((slider) => {
                  return (
                    <RateSliderField
                      key={slider.key}
                      id={slider.id}
                      label={slider.label}
                      max={slider.max}
                      step={slider.step}
                      value={rates[slider.key]}
                      isActive={activeRate === slider.key}
                      onActivate={() => {
                        setActiveRate(slider.key);
                      }}
                      onChange={moves[slider.key].onChange}
                      onNudge={moves[slider.key].onNudge}
                    />
                  );
                })}
                {fields?.sliderDisclaimer && (
                  <Disclaimer
                    {...inspectorProps({ fieldId: 'sliderDisclaimer' })}
                  >
                    <RichTextSwitch
                      sys={sys}
                      content={fields.sliderDisclaimer}
                      nodes={{
                        p: {
                          variant: 'p',
                          component: 'p',
                          fieldId: 'sliderDisclaimer',
                        },
                      }}
                    />
                  </Disclaimer>
                )}
              </Stack>
              <LendScoreCard
                fields={fields}
                sys={sys}
                metricKey={activeRate}
                onCtaClick={trackCtaClick}
                onMetricChange={(key) => {
                  trackEngaged('metric', key);
                  setActiveRate(key);
                }}
                projection={projection}
              />
            </Stack>
          </Column>
        </Row>
      </Container>
    </Section>
  );
};
